import { inject, Injectable, Signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import { CreateQueryResult, injectMutation, injectQuery, QueryClient } from '@tanstack/angular-query-experimental';
import { lastValueFrom, map } from 'rxjs';

import { ApiUser, ApiUsersResponse, ApiUsersResult, EditUserForm, ProfileForm, UserForm } from '@core/models/api/user.api.model';
import { AvatarApi } from '@core/services/api/avatar.api';
import { BaseApi } from '@core/services/api/base.api';
import { PagingParams } from '@core/models/paging.model';
import { DEFAULT_QUERY_OPTIONS } from '@shared/helpers/query.helper';

@Injectable({ providedIn: 'root' })
export class UserApi extends BaseApi {
    protected override readonly baseUrl = `${environment.apiUrl}/users`;
    private readonly baseQueryKey = 'users';
    private readonly queryClient = inject(QueryClient);
    private readonly avatarApi = inject(AvatarApi);

    public upsertUser() {
        return injectMutation<null, HttpErrorResponse, UserForm>(() => ({
            mutationFn: (userForm) => {
                return lastValueFrom(this.post(this.baseUrl, userForm));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    public getOneById(id: Signal<string | undefined>): CreateQueryResult<ApiUser, HttpErrorResponse> {
        return injectQuery<ApiUser, HttpErrorResponse>(() => ({
            enabled: !!id(),
            queryKey: [this.baseQueryKey, id()],
            queryFn: () => lastValueFrom(
                this.get<ApiUser>(this.baseUrl + '/' + id())
            ),
            ...DEFAULT_QUERY_OPTIONS,
        }));
    }

    public getMany(paging: Signal<PagingParams>): CreateQueryResult<ApiUsersResult, HttpErrorResponse> {
        return injectQuery<ApiUsersResult, HttpErrorResponse>(() => ({
            queryKey: [this.baseQueryKey, paging()],
            queryFn: () => lastValueFrom(
                this.get<ApiUsersResponse>(this.baseUrl, this.getHttpParams(paging())).pipe(
                    map((response) => this.parsePagedResponse(response)),
                ),
            ),
            ...DEFAULT_QUERY_OPTIONS,
        }));
    }

    public getAll(enable: Signal<boolean>): CreateQueryResult<ApiUsersResult, HttpErrorResponse> {
        return injectQuery<ApiUsersResult, HttpErrorResponse>(() => ({
            enabled: enable(),
            queryKey: [this.baseQueryKey],
            queryFn: () => lastValueFrom(
                this.get<ApiUsersResponse>(this.baseUrl, this.getHttpParams({
                    page: 0,
                    size: 999999, // Big number but not too big
                })).pipe(
                    map((response) => this.parsePagedResponse(response)),
                )
            ),
            ...DEFAULT_QUERY_OPTIONS,
        }));
    }

    public updateUserRole() {
        return injectMutation<ApiUser, HttpErrorResponse, { userId: string; role: string }>(() => ({
            mutationFn: ({ userId, role }) => {
                return lastValueFrom(this.patch<ApiUser>(`${this.baseUrl}/${userId}`, { role }));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    public updateUser() {
        return injectMutation<ApiUser, HttpErrorResponse, { userId: string; data: EditUserForm }>(() => ({
            mutationFn: ({ userId, data }) => {
                return lastValueFrom(this.patch<ApiUser>(`${this.baseUrl}/${userId}`, data));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    public updateProfile() {
        return injectMutation<ApiUser, HttpErrorResponse, ProfileForm>(() => ({
            mutationFn: (data) => {
                return lastValueFrom(this.patch<ApiUser>(`${this.baseUrl}/me`, data));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    public updateUserStatus() {
        return injectMutation<ApiUser, HttpErrorResponse, { userId: string; status: string }>(() => ({
            mutationFn: ({ userId, status }) => {
                return lastValueFrom(this.put<ApiUser>(`${this.baseUrl}/${userId}/status`, { status }));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    public deleteUser() {
        return injectMutation<void, HttpErrorResponse, string>(() => ({
            mutationFn: (userId) => {
                return lastValueFrom(this.delete<void>(`${this.baseUrl}/${userId}`));
            },
            onSuccess: async () => {
                await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
            },
        }));
    }

    /** Syncs another user's avatar from the identity provider. Needs write:users and follows the role hierarchy. */
    public syncAvatar(userId: string): Promise<void> {
        return this.requestAvatarSync(`${this.baseUrl}/${userId}/avatar/sync`);
    }

    /** Syncs the current user's avatar from the identity provider. Needs only read:users, so members can use it too. */
    public syncOwnAvatar(): Promise<void> {
        return this.requestAvatarSync(`${this.baseUrl}/me/avatar/sync`);
    }

    private async requestAvatarSync(url: string): Promise<void> {
        await lastValueFrom(this.post<void>(url));
        await this.avatarApi.invalidateAll();
        await this.queryClient.invalidateQueries({ queryKey: [this.baseQueryKey] });
    }

    private parsePagedResponse(response: ApiUsersResponse): ApiUsersResult {
        return {
            users: response._embedded.users,
            page: response.page,
        };
    }
}
